import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, Collapse, Descriptions } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDataGrid } from './DataGridContext';
import { useIsSmallScreen } from '../../utils/useMediaQuery';

// Reusable "expand a row into a detail card" mechanism shared by every grid.
// Instead of an inline expandable row (incompatible with virtualization), each
// opened row becomes a collapsible card in a right-side, resizable drawer, and
// the opened rows are highlighted in the table.
//
// Usage in a grid:
//   const { expandColumn, drawer, refreshDetail } = useRowDetailDrawer({
//       name: 'study',
//       columns: myColumns,                 // shown as a full-info Descriptions
//       getTitle: (r) => `${r.patientName}`, // card header
//       renderDetail: (r) => <MyDetail record={r} />,
//   })
//   // put expandColumn first in setDataGridProps({ columns: [expandColumn, ...myColumns] })
//   // render {drawer} next to <DataGrid/>
export function useRowDetailDrawer({
    name = 'detail',
    rowKey = 'id',
    columns = [],
    showInfo = true,
    getTitle,
    renderInfo,
    renderDetail,
    highlightColor = '#91caff',
} = {}) {
    const { t } = useTranslation();
    const { tableId, resultModel, renderItem } = useDataGrid();
    const WIDTH_KEY = `detail-drawer-width-${name}`;

    // Dar ekranda (tablet / yatay telefon) sabit genişlikli, maskesiz ve fareyle
    // boyutlandırılan bir yan panel kullanılamaz: sayfayı örter, dokunmatikte
    // kolu tutulamaz. Bu genişliklerde panel tam ekran + maskeli olur.
    // Eşik LayoutWithSider ile aynı (992) → filtre çekmeceye düştüğünde detay da mobil davranır.
    const isSmallScreen = useIsSmallScreen(992);

    const [openItems, setOpenItems] = useState([]);

    // Keep the opened cards in sync with fresh grid data. Without this the drawer
    // (its info Descriptions and any row-menu rendered inside it) keeps the record
    // captured when the row was opened, so actions like "read" then a shortcut act
    // on a stale record. Only the record object is swapped (same id) — the detail
    // sub-grids keep their studyId prop, so they don't remount or reset tabs.
    const gridData = resultModel?.data;
    useEffect(() => {
        if (!gridData || !gridData.length) return;
        setOpenItems(prev => {
            let changed = false;
            const next = prev.map(item => {
                const fresh = gridData.find(d => String(d[rowKey]) === String(item[rowKey]));
                if (fresh && fresh !== item) { changed = true; return fresh; }
                return item;
            });
            return changed ? next : prev;
        });
    }, [gridData, rowKey]);
    const [activeKeys, setActiveKeys] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [drawerWidth, setDrawerWidth] = useState(() => {
        const saved = parseInt(localStorage.getItem(WIDTH_KEY), 10);
        const width = saved && saved > 0 ? saved : 560;
        // Geniş ekranda kaydedilen genişlik dar ekranda ekranı taşırabilir → kırp.
        return Math.min(width, window.innerWidth);
    });

    const open = useCallback((record) => {
        setOpenItems(prev => (prev.find(x => x[rowKey] === record[rowKey]) ? prev : [...prev, record]));
        // Expand only the just-opened card; others collapse to a one-line header.
        setActiveKeys([String(record[rowKey])]);
    }, [rowKey]);

    const close = useCallback((id) => {
        setOpenItems(prev => prev.filter(x => String(x[rowKey]) !== String(id)));
        setActiveKeys(prev => prev.filter(k => k !== String(id)));
    }, [rowKey]);

    const closeAll = useCallback(() => {
        setOpenItems([]);
        setActiveKeys([]);
    }, []);

    // Click-away: çekmece açıkken, dışına yapılan (neredeyse) her tıklama onu kapatır.
    // Her mousedown'da kapatırız — AŞAĞIDAKİLER HARİÇ:
    //  - satır aç/genişlet düğmesi (yeni bir kart açar/ekler),
    //  - çekmecenin yeniden boyutlandırma kolu,
    //  - ANA GRİD SATIRLARI: satır seçip gezinirken çekmece açık kalsın,
    //  - FİLTRE FORMUNDAKİ KONTROLLER (input/select/tarih/switch/buton): kutuya
    //    girip değer seçerken kapanması saçma olur; formun boş alanı yine kapatır,
    //  - etkileşimde kalması gereken yüzen katmanlar (dropdown, select/tarih
    //    açılırı, popover, tooltip, modal, bildirim) — kart içinden açılanlar dâhil,
    //  - çekmece içindeki gerçek bir kart (Collapse paneli). Çekmecenin kendi gri
    //    gövdesi / başlığı yine de kapatır.
    // Böylece sol filtrenin BOŞ alanına, genel yerleşime (LayoutWithSider) ve
    // çekmecenin gri alanına tıklamak kapatır; grid satırı, filtre kontrolleri ve
    // kart içeriği kapatmaz.
    useEffect(() => {
        if (!openItems.length) return;
        const KEEP_OPEN_OVERLAYS =
            '.ant-dropdown, .ant-select-dropdown, .ant-picker-dropdown, ' +
            '.ant-cascader-dropdown, .ant-popover, .ant-tooltip, ' +
            '.ant-modal-root, .ant-notification, .ant-message, .ant-image-preview-root, ' +
            // Dar ekranda satır menüsü de bir çekmece (bkz. RowMenu): kart içinden
            // açılıyor, "çekmece dışı tıklama" sayılıp kartları kapatmasın.
            '.grid-dropdown-drawer';
        const onMouseDown = (e) => {
            const el = e.target;
            if (!el || typeof el.closest !== 'function') return;

            // Aç/genişlet düğmesi ve boyutlandırma kolu: asla kapatma.
            if (el.closest('.ant-table-row-expand-icon')) return;
            if (el.closest('[data-detail-drawer-resizer]')) return;

            // Ana grid satırına tıklamak çekmeceyi kapatmaz (satır seçimi/gezinme sürsün).
            if (el.closest(`[id="${tableId}"] tr[data-row-key]`)) return;

            // Filtre formundaki bir kontrole tıklamak kapatmaz (boş alanı yine kapatır).
            if (el.closest('.grid-filter-form') && el.closest(
                'input, textarea, button, .ant-input, .ant-input-affix-wrapper, ' +
                '.ant-input-number, .ant-select, .ant-picker, .ant-switch, ' +
                '.ant-radio-group, .ant-radio-wrapper, .ant-checkbox-wrapper, .ant-btn'
            )) return;

            // Yüzen katmanlar (kart içinden açılanlar dâhil): açık kalsın.
            if (el.closest(KEEP_OPEN_OVERLAYS)) return;

            // Çekmecenin içi: yalnızca gerçek bir kart üstünde açık tut; gri gövde /
            // başlık alanı yine kapatır.
            if (el.closest('.ant-drawer-content')) {
                if (el.closest('.ant-collapse-item')) return;
                closeAll();
                return;
            }

            // Başka her yer (filtre paneli, yerleşim, sayfa arka planı).
            closeAll();
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [openItems.length, closeAll, tableId]);

    const refreshDetail = useCallback(() => setRefreshKey(k => k + 1), []);

    // Satır menüleri bir kayıt için renderExpandedRow(id) dispatch eder (StudyRowMenu vb.).
    // Bu sinyali eskiden yalnızca satır-içi genişletme (RenderableExpandRow) dinliyordu;
    // çekmece de dinlesin ki menüden/modaldan yapılan değişiklik açık kartta görünsün.
    // openItems bir ref üzerinden okunur: efekt yalnızca yeni sinyalde çalışsın, kart
    // listesi her değiştiğinde detayı boş yere remount etmesin.
    const openItemsRef = useRef(openItems);
    openItemsRef.current = openItems;
    const renderKey = renderItem?.key;
    const renderRecordId = renderItem?.recordId;
    useEffect(() => {
        if (!renderKey) return;
        // recordId verilmemişse hepsini, verilmişse yalnızca o kart açıksa tazele.
        const affectsOpenCard = !renderRecordId
            || openItemsRef.current.some(x => String(x[rowKey]) === String(renderRecordId));
        if (affectsOpenCard)
            refreshDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderKey]);

    const startResize = useCallback((e) => {
        e.preventDefault();
        document.body.style.userSelect = 'none';
        const onMove = (ev) => {
            const w = Math.min(Math.max(360, window.innerWidth - ev.clientX), window.innerWidth - 80);
            setDrawerWidth(w);
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.userSelect = '';
            setDrawerWidth(w => {
                localStorage.setItem(WIDTH_KEY, String(w));
                return w;
            });
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [WIDTH_KEY]);

    // Native antd expand-icon "+" that opens the row's detail card.
    const expandColumn = useMemo(() => ({
        title: '',
        key: '__expand__',
        width: 40,
        align: 'center',
        render: (_, record) => (
            <button
                type={'button'}
                className={'ant-table-row-expand-icon ant-table-row-expand-icon-collapsed'}
                aria-label={t('Expand')}
                onClick={(e) => {
                    e.stopPropagation();
                    open(record);
                }}
            />
        )
    }), [open, t]);

    const drawer = (
        <>
            {/* Highlight the open rows in the table (CSS => survives virtualization) */}
            {openItems.length > 0 && (
                <style>
                    {openItems.map(r =>
                        `[id="${tableId}"] tr[data-row-key="${r[rowKey]}"] > td{background:${highlightColor} !important;}`
                    ).join('')}
                </style>
            )}

            {openItems.length > 0 && !isSmallScreen && (
                <div
                    data-detail-drawer-resizer
                    onMouseDown={startResize}
                    title={t('Drag to resize')}
                    style={{
                        position: 'fixed',
                        top: 0,
                        bottom: 0,
                        right: drawerWidth,
                        width: 6,
                        cursor: 'col-resize',
                        // Below antd modals/drawer (z-index 1000) so it never sits
                        // on top of an open modal; it doesn't overlap the drawer
                        // panel (it's just to its left) so this is safe.
                        zIndex: 999,
                        background: 'rgba(0,0,0,0.06)'
                    }}
                />
            )}

            <Drawer
                placement={'right'}
                width={isSmallScreen ? '100%' : drawerWidth}
                open={openItems.length > 0}
                // Geniş ekranda maskesiz: panel açıkken tabloyla çalışmaya devam edilir.
                // Dar ekranda panel zaten tüm ekranı kaplıyor → maske + dışına dokununca kapansın.
                mask={isSmallScreen}
                maskClosable={isSmallScreen}
                onClose={closeAll}
                title={`${t('Details')} (${openItems.length})`}
                bodyStyle={{ padding: 8, background: 'var(--surface-2)' }}
            >
                <Collapse
                    activeKey={activeKeys}
                    onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
                    style={{ background: 'transparent' }}
                >
                    {openItems.map((record) => (
                        <Collapse.Panel
                            key={String(record[rowKey])}
                            header={(
                                <span style={{ fontSize: 12 }}>
                                    {getTitle ? getTitle(record) : String(record[rowKey])}
                                </span>
                            )}
                            extra={(
                                <span
                                    onClick={(e) => { e.stopPropagation(); close(record[rowKey]); }}
                                    style={{ cursor: 'pointer', padding: '0 4px' }}
                                >✕</span>
                            )}
                        >
                            {/* No inner scroll — the card grows to its natural
                                height and the Drawer body is the only scroller. */}
                            <div>
                                {renderInfo
                                    ? renderInfo(record)
                                    : (showInfo && columns.length > 0 && (
                                        <Descriptions
                                            size={'small'}
                                            // İki sütun dar ekranda 120px etiket + değeri sığdıramıyor → tek sütun.
                                            column={isSmallScreen ? 1 : 2}
                                            bordered
                                            labelStyle={{
                                                fontWeight: 600,
                                                background: 'var(--surface-2)',
                                                width: 120,
                                                fontSize: 12,
                                                whiteSpace: 'nowrap'
                                            }}
                                            contentStyle={{ fontSize: 12, wordBreak: 'break-word' }}
                                        >
                                            {columns
                                                .filter(col => col.title)
                                                .map((col, i) => (
                                                    <Descriptions.Item key={col.key || i} label={col.title}>
                                                        {col.render
                                                            ? col.render(record[col.dataIndex], record, 0)
                                                            : record[col.dataIndex]}
                                                    </Descriptions.Item>
                                                ))}
                                        </Descriptions>
                                    ))}

                                {renderDetail && (
                                    <div key={`${record[rowKey]}-${refreshKey}`} style={{ marginTop: (renderInfo || showInfo) ? 8 : 0 }}>
                                        {renderDetail(record)}
                                    </div>
                                )}
                            </div>
                        </Collapse.Panel>
                    ))}
                </Collapse>
            </Drawer>
        </>
    );

    return { expandColumn, drawer, open, close, closeAll, refreshDetail, openItems, activeKeys };
}
